<template>
  <article
    class="dashboard-segment-card"
    role="button"
    tabindex="0"
    @click="goToSegment"
    @keydown.enter="goToSegment"
    @keydown.space.prevent="goToSegment"
  >
    <div class="dashboard-segment-card__header">
      <span
        class="dashboard-segment-card__swatch"
        :class="`dashboard-segment-card__swatch--${color}`"
        aria-hidden="true"
      />
      <h3 class="dashboard-segment-card__name">{{ name }}</h3>
    </div>
    <p v-if="role" class="dashboard-segment-card__role">{{ role }}</p>
    <p class="dashboard-segment-card__count">{{ personaCount }} personas</p>

    <!-- Options menu — only for non-system segments -->
    <div
      v-if="!isSystem"
      class="dashboard-segment-card__options"
      :class="{ 'dashboard-segment-card__options--always-visible': isMobile }"
    >
      <div
        ref="triggerRef"
        class="dashboard-segment-card__options-trigger"
        role="button"
        :aria-label="t('segments.list.optionsAria', { label: name })"
        aria-haspopup="menu"
        :aria-expanded="menuOpen ? 'true' : 'false'"
        tabindex="0"
        @click.stop="menuOpen = !menuOpen"
        @keydown.enter.stop="menuOpen = !menuOpen"
        @keydown.space.prevent.stop="menuOpen = !menuOpen"
      >
        <KdsIcon name="more-horizontal" :size="16" class="dashboard-segment-card__options-trigger-icon" />

        <!--
          Disable Quasar's auto-attach click handler on the anchor — we manage open state
          via our own @click handler to avoid double-toggle / immediate close.
        -->
        <q-menu
          v-model="menuOpen"
          no-parent-event
          auto-close
          anchor="bottom right"
          self="top right"
          class="dashboard-segment-card__options-dropdown"
          @hide="onMenuHide"
        >
          <div
            ref="menuListRef"
            role="menu"
            class="dashboard-segment-card__options-list"
            @keydown="onMenuKeydown"
          >
            <!-- Edit segment -->
            <div
              role="menuitem"
              class="dashboard-segment-card__options-item"
              tabindex="0"
              @click="onEditClick"
              @keydown.enter.stop="onEditClick"
              @keydown.space.prevent.stop="onEditClick"
            >
              <KdsIcon name="settings-2" :size="16" />
              <span>{{ t('segments.actions.editSegment') }}</span>
            </div>

            <hr class="dashboard-segment-card__options-separator" />

            <!-- Delete segment -->
            <div
              role="menuitem"
              class="dashboard-segment-card__options-item dashboard-segment-card__options-item--danger"
              tabindex="0"
              @click="onDeleteClick"
              @keydown.enter.stop="onDeleteClick"
              @keydown.space.prevent.stop="onDeleteClick"
            >
              <KdsIcon name="trash-2" :size="16" />
              <span>{{ t('segments.actions.deleteSegment') }}</span>
            </div>
          </div>
        </q-menu>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { KdsIcon } from '@/kds';
import type { PersonaColorKey } from '@/types/segment';

interface Props {
  segmentId: string;
  color: PersonaColorKey;
  name: string;
  role: string;
  personaCount: number;
  isSystem?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSystem: false
});

const emit = defineEmits<{
  edit: [segmentId: string];
  delete: [segmentId: string];
}>();

const { t } = useI18n();
const router = useRouter();
const $q = useQuasar();

const menuOpen = ref(false);
const menuListRef = ref<HTMLElement | null>(null);
const triggerRef = ref<HTMLElement | null>(null);

const isMobile = computed(() => $q.screen.lt.sm);

const goToSegment = (): void => {
  void router.push({ name: 'segment-assign', params: { id: props.segmentId } });
};

const onEditClick = (): void => {
  menuOpen.value = false;
  emit('edit', props.segmentId);
};

const onDeleteClick = (): void => {
  menuOpen.value = false;
  emit('delete', props.segmentId);
};

/** Returns focus to the trigger button when the menu closes (a11y keyboard flow). */
const onMenuHide = (): void => {
  menuOpen.value = false;
  triggerRef.value?.focus();
};

/**
 * ARIA menu keyboard navigation (ArrowDown / ArrowUp).
 * Moves focus to the next/previous focusable menuitem with wrap-around.
 */
const onMenuKeydown = (event: KeyboardEvent): void => {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
  event.preventDefault();

  const list = menuListRef.value;
  if (!list) return;

  const items = Array.from(
    list.querySelectorAll<HTMLElement>('[role="menuitem"]:not([tabindex="-1"])')
  );
  if (items.length === 0) return;

  const currentIndex = items.findIndex((el) => el === document.activeElement);

  let nextIndex: number;
  if (event.key === 'ArrowDown') {
    nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
  } else {
    nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
  }

  items[nextIndex]?.focus();
};
</script>

<style scoped lang="scss">
.dashboard-segment-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-4);
  background: var(--paper);
  border: var(--border);
  border-radius: var(--r-4);
  cursor: pointer;
  transition:
    background-color var(--d-base) var(--ease-out),
    border-color var(--d-base) var(--ease-out),
    box-shadow var(--d-base) var(--ease-out);

  &:hover {
    background: var(--paper-3);
    border-color: var(--mist-2);
    box-shadow: var(--shadow-2);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:hover .dashboard-segment-card__options {
    opacity: 1;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  &__swatch {
    width: 20px;
    height: 20px;
    border-radius: var(--r-pill);
    flex-shrink: 0;

    &--clay { background: var(--persona-clay); }
    &--moss { background: var(--persona-moss); }
    &--fog { background: var(--persona-fog); }
    &--rose { background: var(--persona-rose); }
    &--sand { background: var(--persona-sand); }
    &--slate { background: var(--persona-slate); }
    &--plum { background: var(--persona-plum); }
  }

  &__name {
    margin: 0;
    font-size: var(--fs-h3);
    font-weight: var(--fw-semibold);
    color: var(--ink);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__role {
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--fs-body-sm);
    color: var(--stone);
  }

  &__count {
    margin: 0;
    font-family: var(--font-mono);
    font-size: var(--fs-meta);
    color: var(--stone);
  }

  &__options {
    position: absolute;
    top: var(--space-3);
    right: var(--space-3);
    z-index: 1;
    opacity: 0;
    transition: opacity var(--d-fast) var(--ease-out);

    &--always-visible {
      opacity: 1;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
      opacity: 1;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &__options-trigger {
      transition: none;
    }
  }

  &__options-trigger {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    // Visual size: 28×28. Transparent padding expands the touch target to ≥44×44 (a11y mobile).
    width: 28px;
    height: 28px;
    padding: 8px;
    margin: -8px;
    border-radius: var(--r-2);
    background: transparent;
    color: var(--ink);
    cursor: pointer;
    transition: background-color var(--d-fast) var(--ease-out);

    &:hover {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }
  }

  &__options-trigger-icon {
    pointer-events: none;
  }

  &__options-dropdown {
    border-radius: var(--r-4);
    box-shadow: var(--shadow-2);
    overflow: hidden;
    min-width: 196px;
  }

  &__options-list {
    display: flex;
    flex-direction: column;
    padding: var(--space-2) 0;
  }

  &__options-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-4);
    cursor: pointer;
    font-size: var(--fs-body-sm);
    color: var(--ink);
    user-select: none;
    transition: background var(--d-fast) var(--ease-out);

    &:hover {
      background: var(--paper-3);
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
    }

    // D3: replaced rgba(255,59,59,0.06) with color-mix token approach.
    &--danger {
      color: var(--danger);

      &:hover {
        background: color-mix(in srgb, var(--danger) 12%, transparent);
      }
    }
  }

  &__options-separator {
    border: none;
    border-top: var(--border);
    margin: var(--space-2) 0;
  }
}
</style>
